import {Component, Input, OnDestroy, OnInit, AfterViewInit, ViewChild} from "@angular/core";
import {HostingService} from "../../../../websites/website/hosting/hosting.service";
import {BackupService} from "../../../../websites/website/backup/backup.service";
import {filter, first, switchMap, takeUntil} from "rxjs/operators";
import * as fromStore from "../../../../store";
import {Website} from "../../../../websites/website/website";
import {NotifierComponent} from "../../../../shared/components/notifier/notifier.component";
import {Notification} from "../../../../shared/components/notifier/notification";
import {NotificationType} from "../../../../shared/components/notifier/notification-type";
import {Store} from "@ngrx/store";
import {CompanyInfo, DbStoreState} from "../../../../store";
import {DashboardUtils} from "../../../../shared/utils/dashboard-utils";
import {Utils} from "../../../../shared/utils/utils";
import {User} from "../../../../user/user";
import {UserSubscription} from "../../../../user/user-subscription";
import {DbActiveModal} from "../../../../modules/modal/modal-ref";
import {DbResponse} from "../../../../shared/interceptors/db-response";
import {BuilderV2Service} from "../../../../builder-v2/builder-v2.service";
import {BuilderSectionsInteractionService} from "../../../../builder-v2/builder-sections-interaction.service";
import {BuilderV2Component} from "../../../../builder-v2/builder-v2.component";
import {BuilderHistoryService} from "../../../../builder-v2/builder-history.service";
import {BuilderStepsEnum} from "../../../../builder-v2/builder-tour.enum";
import {Subject} from "rxjs";
import {WebsitePromptComponent} from "../../../../websites/website-prompt/website-prompt.component";
import {ReCaptchaV3Service} from "ngx-captcha";
import {Config} from "../../../../config/config";
import {ActivityStatus} from "../../../../activities/activity";
import {
  CreateAiAssistantService
} from "../../../../components/pages/setup/create-ai/create-ai-assistant/create-ai-assistant.service";
import {TriggersService} from "../../../../builder-v2/services/triggers.service";
import {CreateWebsiteHelpersService} from "../../../../services/local/create-website-helpers.service";
import {ResellerCidService} from "../../../services/reseller-cid/reseller-cid.service";

@Component({
    selector: 'db-website-creation-states-dialog',
    templateUrl: './website-creation-states-dialog.component.html',
    styleUrls: ['./website-creation-states-dialog.component.scss'],
    standalone: false
})
export class WebsiteCreationStatesDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('builderV2') builderV2: BuilderV2Component;
  @ViewChild('websitePrompt') websitePrompt: WebsitePromptComponent;

  @Input() region: string = "us-central1";
  @Input() user: User;
  @Input() companyName?: string;
  @Input() userSubscription: UserSubscription;
  @Input() description?: string;
  @Input() builderType: 'wvc' | 'section_based';
  @Input() website?: Website;
  @Input() websiteId?: number;
  @Input() step: 'install-package' | 'regenerate' | 'provide-description' | 'outline' | 'section-based-loading' | 'remix';
  @Input() sourceDomainId?: number;
  @Input() siteUrl?: string;
  @Input() images?: any[];
  @Input() figmaUrl?: string;
  @Input() uniqueId?: string;
  @Input() isMobileAutoGenerate?: boolean;

  builderStep = BuilderStepsEnum;
  triggerDescriptionFlow: {
    description: string;
    industryName?: string;
    websiteType?: string;
    images?: any[];
    figmaUrl?: string;
    uniqueId?: string;
    // Pre-generated data
    enhancedDescription?: string;
    businessName?: string;
    businessType?: string;
    outline?: any;
    styles?: any;
  };
  public companyInfo: CompanyInfo;

  private _destroy$ = new Subject<void>();
  private isMobile: boolean = false;

  get useWebsiteGenerationPlanner(): boolean {
    return Utils.useWebsiteGenerationPlanner(this.userSubscription, this.builderType);
  }

  constructor(
    private _hostingService: HostingService,
    private _store: Store<DbStoreState>,
    private _builderService: BuilderV2Service,
    private readonly _activeModal: DbActiveModal,
    private _builderInteractionsService: BuilderSectionsInteractionService,
    private _builderHistoryService: BuilderHistoryService,
    private _createAiAssistantService: CreateAiAssistantService,
    private _reCaptchaV3Service: ReCaptchaV3Service,
    private _triggersService: TriggersService,
    private _createWebsiteHelpersService: CreateWebsiteHelpersService,
    private _backupService: BackupService,
    private _resellerCidService: ResellerCidService
  ) {}

  ngOnInit() {
    this.isMobile = window.innerWidth <= 991;

    if (!this.userSubscription) {
      this._store.select(fromStore.getUserSubscription)
        .pipe(filter((userSubscription: UserSubscription) => !!userSubscription), first(), takeUntil(this._destroy$))
        .subscribe((userSubscription: UserSubscription) => {
          this.userSubscription = userSubscription;
        });
    }

    if (this.description && this.builderType === 'section_based') {
      this._builderInteractionsService.prefilledDescription.next(this.description);
    }

    this._store.select(fromStore.getCompanyInfoLoaded)
        .pipe(
            filter((loaded: boolean) => loaded),
            switchMap(() => this._store.select(fromStore.getCompanyInfo)),
            first()
        )
        .subscribe((companyInfo: any) => {
          this.companyInfo = companyInfo;
        });

    this._getActivities();

    // Listen to builder step changes to resize dialog when SHOW_FINALIZE is active
    this._builderHistoryService.builderStep
      .pipe(takeUntil(this._destroy$))
      .subscribe(builderStep => {
        if (builderStep === BuilderStepsEnum.SHOW_FINALIZE) {
          this._activeModal.resize(350, 500);
        }
      });

    // Listen to generate loading state to resize dialog and show prefilled-generation-finalize
    this._builderInteractionsService.generatePrefilledSiteLoading
      .pipe(takeUntil(this._destroy$))
      .subscribe(isLoading => {
        // Don't resize if dialog is being prepared for fresh open from website prompt
        const isPreparedForFreshOpen = this._builderInteractionsService.prepareDialogForFreshOpen.value;
        if (this.builderType === 'section_based' && !isPreparedForFreshOpen && !this.isMobileAutoGenerate && !this.isMobile) {
          this._activeModal.hideCloseBtn(false);
        }
        if (isLoading && this.builderType === 'section_based' && !isPreparedForFreshOpen) {
          this._activeModal.resize(344, 500);
        }
      });

    switch (this.step) {
      case 'install-package':
        if (this.builderType === 'wvc') {
          this._createWebsite();
        }
        this._activeModal.resize(228, 500);
        this._activeModal.hideCloseBtn(true);
        this._activeModal.setBackdrop('static');
        break;

      case 'outline':
        // Initially hide close button, will show in ngAfterViewInit after builder-v2 is rendered
        this._activeModal.hideCloseBtn(false);
        this._activeModal.setBackdrop(true);
        if (this.builderType === 'section_based') {
          this._handleClassicBuilderFlow();
        }
        break;

      case 'regenerate':
        if (this.builderType === 'wvc') {
          if (this.website?.builderType) {
            this._navigateToEditor(true);
          }
        }
        this._activeModal.resize(262, 700);
        break;

      case 'remix':
        this._activeModal.resize(228, 500);
        this._activeModal.hideCloseBtn(true);
        this._activeModal.setBackdrop('static');
        this._remixWebsite();
        break;
    }
  }

  private _remixWebsite(): void {
    if (this.websiteId && this.sourceDomainId) {
      this._backupService.restoreFromDomain(this.websiteId, this.sourceDomainId)
        .pipe(filter((response: DbResponse) => !!response), first())
        .subscribe((response: DbResponse) => {
          if (response.success) {
            this._monitorRestoreActivity(this.siteUrl);
          } else {
            this.errorHandler();
            this._activeModal.close();
          }
        });
      return;
    }

    if (!this.websiteId && this.sourceDomainId) {
      this._hostingService.createWebsiteForTrialUsers({siteTitle: 'My Website', wvc_import: 1}, false)
        .pipe(filter(response => !!response), first())
        .subscribe(response => {
          if (!response.success) {
            this._activeModal.close();
            return this.errorHandler();
          }
          this.websiteId = response.data?.domain_id || null;
          const siteUrl = response.data?.site_url || null;
          if (this.websiteId) {
            this._backupService.restoreFromDomain(this.websiteId, this.sourceDomainId)
              .pipe(filter((res: DbResponse) => !!res), first())
              .subscribe((res: DbResponse) => {
                if (res.success) {
                  this._monitorRestoreActivity(siteUrl);
                } else {
                  this.errorHandler();
                  this._activeModal.close();
                }
              });
          }
        });
      return;
    }

    this.errorHandler();
  }

  private _monitorRestoreActivity(siteUrl: string): void {
    this._store.select(fromStore.getRestoreOngoingActivity)
      .pipe(
        filter(activity => !!activity && activity.websiteId === this.websiteId),
        takeUntil(this._destroy$)
      )
      .subscribe(activity => {
        if (activity.status === 'done') {
          this._activeModal.close();
          const resolvedSiteUrl = siteUrl ?? this.siteUrl;
          const adminUrl = `${resolvedSiteUrl}/wp-admin/`;
          const editorUrl = `${adminUrl}admin.php?page=wvc-editor&from_dashboard=1`;
          const redirectUrl = DashboardUtils.generateWpAdminRedirectorUrl(this.websiteId, editorUrl, this.user?.email);
          window.location.replace(redirectUrl);
        } else if (activity.status === 'failed') {
          this.errorHandler();
          this._activeModal.close();
        }
      });
  }

  private _getActivities(): void {
    this._store.select(fromStore.getActivities)
      .pipe(
        filter((loaded: any) => loaded),
        switchMap(() => this._store.select(fromStore.getAiGenerateWebsiteOngoingActivity)),
        takeUntil(this._destroy$)
      )
      .subscribe(ongoingActivity => {
        if (!ongoingActivity) {
          return;
        }

        if (ongoingActivity.status === ActivityStatus.RUNNING) {
          this._activeModal.hideCloseBtn(true);
          this._activeModal.setBackdrop(false);
        }
      });
  }


  ngAfterViewInit() {
    // Show close button after view is initialized for outline step
    if (['outline', 'regenerate'].includes(this.step)) {
      setTimeout(() => {
        this._activeModal.hideCloseBtn(false);
      });
    }
    if (this.isMobileAutoGenerate || this.isMobile) {
      setTimeout(() => {
        this._activeModal.hideCloseBtn(true);
      });
    }
  }

  ngOnDestroy() {
    localStorage.removeItem('builder_history');
    localStorage.removeItem('prefilledAiData');
    this._builderHistoryService.resetSubjects();
    this._builderInteractionsService.resetSubjects();
    this._builderHistoryService.removeStateFromStorage();
    this._createAiAssistantService.clearPrefilledData();
    this._triggersService.resetTriggers();
    this._destroy$.next();
    this._destroy$.complete();
  }

  private _handleClassicBuilderFlow() {
    // Handle classic builder flow - prepare trigger data with complete siteInfo
    this.triggerDescriptionFlow = {
      description: this.description,
      industryName: null,
      websiteType: 'basic',
      images: this.images,
      uniqueId: this.uniqueId,
      figmaUrl: this.figmaUrl,
      // Add siteInfo data to prevent it from being overwritten
      businessName: 'My Website',
      businessType: 'basic',
      enhancedDescription: this.description
    };

    // Set builder step to SHOW_CANVAS immediately to display canvas without delay
    this._builderHistoryService.builderStep.next(BuilderStepsEnum.SHOW_CANVAS);

    if (!this.website && !this.websiteId) {
      this._createWebsiteAndShowBuilder();
    } else {
      this._showBuilderUI();
    }
  }

  private _createWebsite(): void {
    // Generate outline first, then create website with that outline
    if (this.website) {
      if (this.website.builderType) {
        this._navigateToEditor(true);
      }
      else {
        const websiteType = this.website?.isEcommerceWebsite  ? 'ecommerce' : 'basic';
        this._builderService.configureWvc(this.website?.id || this.websiteId, 'wvc', websiteType).pipe(
          filter((response: DbResponse) => !!response),
          first()
        ).subscribe((response: DbResponse) => {
          if (response.success) {
            this._navigateToEditor(false, true);
          }
        });
      }
    }
    else {
      this._fetchWebsite();
    }
  }

  private _detectWebsiteType(): Promise<string> {
    return new Promise((resolve, reject) => {
      // Get recaptcha token first
      this._reCaptchaV3Service.execute(Config.googleRecaptcha, 'signup', (token) => {
        // Step 1: Generate enhanced description
        this._builderService.generateAiBuilderDesc(this.description, null)
          .pipe(
            filter((response: DbResponse): boolean => !!response),
            first()
          )
          .subscribe((response: DbResponse) => {
            if (!response.success) {
              this.errorHandler();
              return reject();
            }
            resolve(response.data?.website_type);
          });
      });
    });
  }

  private _fetchWebsite(websiteType?: string): void {
    this._hostingService.createWebsiteForTrialUsers({region: this.region, siteTitle: 'My Website', websiteType, wvc_import: 1}, false)
      .pipe(
        first(response => !!response)
      )
      .subscribe(response => {
        if (!response.success) {
          return this.errorHandler();
        }
        this.websiteId = response.data && response.data.domain_id || null;
        this._createWebsiteObserver();
      });
  }

  private _createWebsiteObserver(): void {
    this._store.dispatch(new fromStore.UpdateUserSubscription());
    this._store.dispatch(new fromStore.LoadWebsites());
    this._getWebsite(this.websiteId)
      .then(() => this._hostingService.whiteListIp(this.website?.id, this.website?.site_url))
      .then(() => this._navigateToEditor(false, true))
      .catch((error) => {
        this.errorHandler();
      });
  }

  private _navigateToEditor(regenerate = false, onboarding = false): void {
    const imageFiles = this.images?.map(img => ({
      url: img.url,
      filename: img.filename || 'image'
    })) || [];

    // Add figma file if exists
    if (this.figmaUrl) {
      imageFiles.push({
        url: this.figmaUrl,
        filename: 'figma-design'
      });
    }
    this._builderService.storeInitialMessage(
      this.website?.id || this.websiteId,
      parseInt(this.user?.id, 10),
      this.description || '',
      imageFiles,
      this.useWebsiteGenerationPlanner
    ).pipe(
      filter((response: DbResponse) => !!response),
      first()
    ).subscribe((response: DbResponse) => {
        if (response.success) {
          this._proceedToEditor(regenerate, onboarding);
        } else {
          this.errorHandler();
        }
      });
  }

  private _proceedToEditor(regenerate = false, onboarding = false): void {
    let websiteUrl = `${this.website?.adminUrl}admin.php?page=wvc-editor`;
    if (regenerate) {
      websiteUrl += `&regenerate=${regenerate}`;
    }
    if (onboarding) {
      websiteUrl += `&onboarding=${onboarding}`;
    }

    if (!this.figmaUrl && this.step !== 'remix') {
      websiteUrl += `&fromSiteInitial=1`;
    }

    // Combine all data into prefilledAiData object
   /* if (this.description || (this.images && this.images.length > 0) || this.figmaUrl) {
      if (this.description) {
        prefilledAiData.initial_description = this.description;
      }

      if (this.images && this.images.length > 0) {
        prefilledAiData.images = this.images.map(img => ({
          url: img.url,
          filename: img.filename || 'image'
        }));
      }

      if (this.figmaUrl) {
        prefilledAiData.figma_url = this.figmaUrl;
      }
    }*/

    const prefilledAiData: any = {};

    const encodedData = encodeURIComponent(JSON.stringify(prefilledAiData));
    websiteUrl = websiteUrl + `&prefilledAiData=${encodedData}`;

    const previewLink = DashboardUtils.generateWpAdminRedirectorUrl(this.website?.id, websiteUrl, this.user?.email);

    const gtag = window['gtag'];
    let navigated = false;
    const navigate = () => {
      if (!navigated) {
        navigated = true;
        window.location.replace(previewLink);
      }
    };

    // Fallback in case gtag callback never fires (blocked, slow, etc.)
    setTimeout(navigate, 2000);

    if (typeof gtag === 'function') {
      const resellerCid = this._resellerCidService.get();
      gtag("event", "creation_start", {
        twdb_user_id: this.user && this.user.id || 0,
        twdb_website_id: this.website?.id,
        ...(resellerCid ? { cid: resellerCid } : {}),
        event_callback: navigate
      });
    } else {
      navigate();
    }

  }

  /** get Website */
  protected _getWebsite(websiteId): Promise<Website> {
    return new Promise<Website>((resolve, reject) => {
      this._store.select(fromStore.getWebsitesLoaded)
        .pipe(
          first(loaded => loaded),
          switchMap(() => this._store.select(fromStore.getWebsite(websiteId))),
          first(website => !!website)
        )
        .subscribe({
          next: (website) => {
            this.website = website;
            this.websiteId = website.id;
            resolve(null);
          },
          error: (error) => {
            reject(error);
          }
        });
    });
  }

  protected errorHandler(): void {
    NotifierComponent.notify(new Notification('Something Went Wrong...', NotificationType.DANGER));

    // Reset processing state in website prompt on error
    if (this.websitePrompt) {
      this.websitePrompt.resetProcessingState();
    }
  }

  public setStep(step: 'install-package' | 'regenerate' | 'provide-description' | 'outline' | 'section-based-loading' | 'remix'): void {
    this.step = step;
    let width: number, height: number, hideCloseBtn: boolean, setBackdrop: boolean | string;
    switch (this.step) {
      case 'install-package':
        width = 500; height = 228; hideCloseBtn = true; setBackdrop = 'static';
        break;

      case 'regenerate':
        width = 700; height = 262; hideCloseBtn = true; setBackdrop = 'static';
        break;

      case 'provide-description':
      case 'outline':
        width = 1320; height = 760; hideCloseBtn = false; setBackdrop = true;
        break;
    }
    this._activeModal.resize(height, width);
    this._activeModal.hideCloseBtn(hideCloseBtn);
  }

  public close() {
    this._activeModal.close();
  }

  public onPromptSend(data: { description: string; region: string; images?: any[]; figmaUrl?: string; uniqueId?: string }): void {
    // Update component data
    this.description = data.description;
    this.region = data.region;
    this.images = data.images;
    this.figmaUrl = data.figmaUrl;
    if (data.uniqueId) {
      this.uniqueId = data.uniqueId;
    }
    if (this.builderType === 'wvc') {
      this.setStep('install-package');
    }
    else {
      // Prepare trigger data for builder-v2 with complete siteInfo data
      this.triggerDescriptionFlow = {
        description: this.description,
        industryName: null,
        websiteType: 'basic',
        images: this.images,
        uniqueId: this.uniqueId,
        figmaUrl: this.figmaUrl,
        // Add siteInfo data to prevent it from being overwritten
        businessName: 'My Website',
        businessType: 'basic',
        enhancedDescription: this.description
      };

      // Set builder step to SHOW_CANVAS immediately to display canvas without delay
      this._builderHistoryService.builderStep.next(BuilderStepsEnum.SHOW_CANVAS);

      if (!this.website && !this.websiteId) {
        this._createWebsiteAndShowBuilder();
      } else {
        this._showBuilderUI();
      }
    }
  }

  private _createWebsiteAndShowBuilder(): void {
    // Show builder UI - builder-v2 will handle website creation via _generateWebsite() based on builderType
    this._showBuilderUI();
  }

  private _showBuilderUI(): void {
    // Initialize siteInfo to prevent null errors in builder component
    const siteInfoData = {
      business_name: 'My Website',
      business_description: this.description,
      business_type: 'basic',
      domain_name: '',
      website_type: 'basic',
      initial_description: this.description
    };

    if (this.images && this.images.length > 0) {
      siteInfoData['images'] = this.images;
    }
    if (this.figmaUrl) {
      siteInfoData['figma_url'] = this.figmaUrl;
    }

    this._builderInteractionsService.siteInfo.next(siteInfoData);
    this._builderHistoryService.builderStep.next(BuilderStepsEnum.SHOW_CANVAS);


    // Set step immediately to show builder canvas without delay
    // Modal dimensions are already set correctly when opening from website-prompt
    this.setStep('outline');

    // Reset processing state
    if (this.websitePrompt) {
      this.websitePrompt.resetProcessingState();
    }
  }

}
